import { useState } from 'react';
import { Alert, Button, ButtonLink, Input, Logo } from '../design/index.js';
import { ConsentBanner } from '../components/ConsentBanner.js';
import { LegalFooter } from '../components/LegalFooter.js';
import { Link } from '../components/Link.js';

/**
 * Signing in.
 *
 * Which of the two screens appears is decided by the server, not by a build
 * flag: `hostedLogin` says whether this deployment has an identity provider
 * configured. With one, the only thing offered is a button that leaves for it.
 * Without one — a local run, the end-to-end suite — a token can be pasted,
 * because there is nowhere to be sent. That is why the field disappears the
 * moment a provider is configured, rather than being hidden behind a flag
 * somebody has to remember to set.
 *
 * Night, like the hero, because this is the threshold of the product rather
 * than a page inside it.
 */
export function SignIn({
  hostedLogin,
  loginUrl,
  error,
  onToken,
}: {
  readonly hostedLogin: boolean;
  readonly loginUrl: string;
  readonly error: string | null;
  readonly onToken: (token: string) => void;
}): React.JSX.Element {
  const [value, setValue] = useState('');

  return (
    <div className="on-dark flex min-h-dvh flex-col bg-violet">
      <header className="shell py-6">
        <Link href="/" className="inline-flex rounded-control">
          <Logo tone="dark" />
          <span className="sr-only">Retour au site</span>
        </Link>
      </header>

      <main className="shell flex flex-1 items-center justify-center py-8">
        <div className="w-full max-w-md">
          <h1 className="text-heading font-bold text-ink-light">Connexion</h1>
          <p className="mt-2 text-ui text-ink-light-muted">
            {hostedLogin
              ? 'Connectez-vous avec le compte de votre établissement.'
              : 'Aucun fournisseur d’identité n’est configuré sur ce déploiement. Collez un jeton pour travailler en local.'}
          </p>

          {error !== null && (
            <div className="mt-6">
              <Alert kind="warning" title="La connexion n’a pas abouti">
                {error}
              </Alert>
            </div>
          )}

          {hostedLogin ? (
            <div className="mt-8">
              {/*
                A link, not a fetch. The sign-in has to be a top-level
                navigation: the provider answers with its own page, and a
                redirect chain followed by XMLHttpRequest ends nowhere a person
                can type a password.
              */}
              <ButtonLink href={loginUrl} tone="dark" variant="primary" size="lg" block>
                Se connecter
              </ButtonLink>
            </div>
          ) : (
            <form
              className="mt-8 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (value.trim() !== '') onToken(value.trim());
              }}
            >
              <Input
                label="Jeton"
                type="password"
                value={value}
                autoComplete="off"
                placeholder="Jeton"
                onChange={(event) => {
                  setValue(event.target.value);
                }}
              />
              <Button type="submit" variant="primary" size="lg" block>
                Continuer
              </Button>
            </form>
          )}
        </div>
      </main>

      {/*
        Here too, and for the reason that matters most: the person deciding
        whether to hand us a credential is exactly the one entitled to read
        first what we do with their data.
      */}
      <div className="shell pb-8">
        <LegalFooter tone="dark" />
      </div>
      <ConsentBanner />
    </div>
  );
}
